import React, { useState, useEffect } from "react";
import LogoCEA from "../assets/images/logo.png";
import "../style/registermonthly.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SyncLoader from "react-spinners/SyncLoader";
import SizeChartModal from "./SizeChartModal";
import DateInput from "./DateInput";

const classOrder = [
  "OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS",
  "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE",
];

const yearlyDefaultClasses = [
  { name: "OT", status: "requested" },
  { name: "NT", status: "requested" },
  { name: "BD", status: "requested" },
  { name: "BLC", status: "requested" },
  { name: "BCMF", status: "requested" },
  { name: "MASTERCLASS", status: "requested" },
  { name: "HH", status: "requested" },
  { name: "CBH", status: "requested" },
  { name: "CBD", status: "requested" },
  { name: "BWG", status: "requested" },
  { name: "MMM", status: "requested" },
  { name: "CONFERENCE", status: "requested" },
];

const emptyStudent = () => ({
  name: "",
  email: "",
  confirmEmail: "",
  phoneNumber: "",
  dateOfBirth: "",
  address: "",
  city: "",
  province: "",
  postalCode: "",
  studentPackage: "",
  role: "",
  roleOther: "",
  classType: "",
  program: "",
  tshirtSize: "",
  startingClass: "OT",
  classes: { className: [] },
  joinedCEA: "No",
  yearJoined: "",
  emailVerify: "",
  verified: false,
  verifying: false,
  oldClassList: null,
  question1: "",
  question2: "",
  question3: "",
  question4: "",
  question5: "",
  collapsed: false,
});

const CeaToChurch = ({ classDetails = [] }) => {
  const navigate = useNavigate();

  const [churchName, setChurchName] = useState("");
  const [picName, setPicName] = useState("");
  const [picEmail, setPicEmail] = useState("");
  const [picConfirmEmail, setPicConfirmEmail] = useState("");
  const [picPhone, setPicPhone] = useState("");
  const [churchCity, setChurchCity] = useState("");
  // Sengaja TANPA default: penanggung jawab harus memilih sendiri cara bayarnya,
  // supaya tidak ada yang ter-submit "full" hanya karena pertanyaannya terlewat.
  const [paymentPlan, setPaymentPlan] = useState("");

  const [students, setStudents] = useState([emptyStudent()]);
  const [currentClass, setCurrentClass] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [batchNum, setBatchNum] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 8 ? now.getFullYear() - 2021 : now.getFullYear() - 2022;
  });

  useEffect(() => {
    axios.get("/config/currentclass")
      .then((res) => { if (res.data.currentClass) setCurrentClass(res.data.currentClass); })
      .catch(() => {});
    // Batch mengikuti "Current Running Batch" yang di-set admin (SIMULATED_BATCH),
    // bukan sekadar hitungan kalender — supaya konsisten dgn RegisterYearly.
    axios.get("/config/simulatedbatch")
      .then((res) => { if (res.data.simulatedBatch) setBatchNum(parseInt(res.data.simulatedBatch)); })
      .catch(() => {});
  }, []);

  // Kelas sebelum current class (config admin) sudah lewat → disable di Starting Class.
  // Kalau semua opsi student sudah lewat (returning student yang sisa kelasnya
  // sudah lewat semua), opsi dibiarkan aktif supaya tetap bisa ikut batch berikutnya.
  const isClassPassed = (code) => {
    const currentIdx = classOrder.indexOf(currentClass);
    return currentIdx > 0 && classOrder.indexOf(code) < currentIdx;
  };
  const getStartingClassOptions = (s) =>
    (s.verified ? s.classes.className.filter((c) => c.status !== "completed") : yearlyDefaultClasses).map((c) => c.name);
  const isStartingClassDisabled = (s, code) =>
    getStartingClassOptions(s).some((c) => !isClassPassed(c)) && isClassPassed(code);

  useEffect(() => {
    if (!currentClass) return;
    const needsFix = students.some((s) => isStartingClassDisabled(s, s.startingClass));
    if (!needsFix) return;
    setStudents((prev) =>
      prev.map((s) => {
        if (!isStartingClassDisabled(s, s.startingClass)) return s;
        const firstOpen = getStartingClassOptions(s).find((c) => !isClassPassed(c));
        return firstOpen ? { ...s, startingClass: firstOpen } : s;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass, students]);

  const updateStudent = (index, field, value) => {
    setStudents((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  const updateStudentFields = (index, patch) => {
    setStudents((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  // Gabungkan classList lama (student yang sudah verified/returning) dengan
  // kelas yang baru dipilih sekarang — entry lama dipertahankan apa adanya
  // (termasuk status "completed"-nya, supaya tidak dikira perlu dibayar lagi),
  // kelas baru yang belum ada di classList lama ditambahkan sebagai "requested".
  const mergeExistingClassList = (oldClassList, newlySelectedCodes) => {
    const oldEntries = oldClassList?.className || [];
    const oldNames = new Set(oldEntries.map((c) => c.name));
    const fresh = newlySelectedCodes
      .filter((code) => !oldNames.has(code))
      .map((code) => ({ name: code, status: "requested" }));
    const result = [...oldEntries, ...fresh];
    result.sort((a, b) => classOrder.indexOf(a.name) - classOrder.indexOf(b.name));
    return { className: result };
  };

  const handleProgramChange = (index, program) => {
    setStudents((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const newlySelectedCodes = program === "1 Year Program" ? classOrder : [];
        const classes =
          s.verified && s.oldClassList
            ? mergeExistingClassList(s.oldClassList, newlySelectedCodes)
            : program === "1 Year Program"
              ? { className: yearlyDefaultClasses.map((c) => ({ ...c })) }
              : { className: [] };
        const firstGap = classes.className.find((c) => c.status !== "completed");
        return {
          ...s,
          program,
          startingClass: firstGap ? firstGap.name : "OT",
          classes,
        };
      }),
    );
  };

  const FIXED_ROLES = [
    "HITS/Mth Student", "Pastoral", "Staff", "Coach",
    "Care Group Leader (CGL)", "Core Team Leader (CTL)", "Attendees (IFGF BDG)",
  ];

  const handleVerify = async (index) => {
    const s = students[index];
    if (!s.emailVerify) {
      showError("Please input the email used in your previous registration!");
      return;
    }
    updateStudentFields(index, { verifying: true });
    try {
      const response = await axios.get(`/verifydata/${s.emailVerify.trim()}`);
      if (response.data === null) {
        await showError("No previous registration found with this email. Please continue filling the form as a new student.");
        updateStudentFields(index, { verifying: false, joinedCEA: "No", verified: false, yearJoined: "" });
        return;
      }
      if (response.data.msg === "Your data still in approval process.") {
        await Swal.fire({
          text: response.data.msg,
          icon: "info",
          background: "white",
          color: "black",
          backdrop: `rgba(131, 131, 131, 0.5)`,
          confirmButtonColor: "#0004ff",
          confirmButtonText: "OK",
        });
        updateStudentFields(index, { verifying: false, verified: false });
        return;
      }

      const d = response.data;
      const roleOk = FIXED_ROLES.includes(d.role);
      const oldClassList = d.classList ? JSON.parse(d.classList) : { className: [] };
      const newlySelectedCodes = d.program === "1 Year Program" ? classOrder : [];
      const classes = mergeExistingClassList(oldClassList, newlySelectedCodes);
      const firstGap = classes.className.find((c) => c.status !== "completed");

      updateStudentFields(index, {
        verifying: false,
        verified: true,
        name: d.name,
        email: d.email,
        phoneNumber: d.phoneNumber,
        dateOfBirth: d.dateOfBirth,
        address: d.address,
        city: d.city,
        province: d.province,
        postalCode: d.postalCode,
        studentPackage: d.studentPackage,
        role: roleOk ? d.role : "Others",
        roleOther: roleOk ? "" : d.role,
        classType: d.classType,
        program: d.program,
        tshirtSize: d.tshirtSize || "",
        oldClassList,
        classes,
        startingClass: firstGap ? firstGap.name : "OT",
        yearJoined: d.yearJoined || "",
        question1: d.question1 || "",
        question2: d.question2 || "",
        question3: d.question3 || "",
        question4: d.question4 || "",
        question5: d.question5 || "",
      });
    } catch (error) {
      updateStudentFields(index, { verifying: false });
      showError("Something went wrong while verifying this email. Please try again.");
    }
  };

  const handleClassCheckbox = (index, code) => {
    setStudents((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const exists = s.classes.className.find((c) => c.name === code);
        let updated;
        if (exists) {
          updated = s.classes.className.filter((c) => c.name !== code);
        } else {
          updated = [...s.classes.className, { name: code, status: "requested" }];
        }
        updated.sort((a, b) => classOrder.indexOf(a.name) - classOrder.indexOf(b.name));
        return { ...s, classes: { className: updated } };
      }),
    );
  };

  const addStudent = () => {
    setStudents((prev) => [
      ...prev.map((s) => ({ ...s, collapsed: true })),
      emptyStudent(),
    ]);
  };

  const removeStudent = async (index) => {
    if (students.length === 1) return;
    const result = await Swal.fire({
      text: `Remove Student ${index + 1}${students[index].name ? ` (${students[index].name})` : ""}?`,
      icon: "warning",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Remove",
    });
    if (result.isConfirmed) {
      setStudents((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const toggleCollapse = (index) => {
    setStudents((prev) =>
      prev.map((s, i) => (i === index ? { ...s, collapsed: !s.collapsed } : s)),
    );
  };

  const showError = (text) =>
    Swal.fire({
      text,
      icon: "error",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      confirmButtonColor: "#0004ff",
      confirmButtonText: "OK",
    });

  const validate = () => {
    if (!churchName || !picName || !picEmail || !picPhone) {
      showError("Please complete the church & contact person data!");
      return false;
    }
    if (!paymentPlan) {
      showError("Please choose a payment option for this registration!");
      return false;
    }
    if (picEmail.trim().toLowerCase() !== picConfirmEmail.trim().toLowerCase()) {
      showError("The contact person email and its confirmation do not match!");
      return false;
    }
    for (let i = 0; i < students.length; i++) {
      const s = students[i];
      const label = `Student ${i + 1}${s.name ? ` (${s.name})` : ""}`;
      if (
        !s.name || !s.email || !s.phoneNumber || !s.dateOfBirth || !s.address ||
        !s.city || !s.province || !s.postalCode || !s.studentPackage ||
        !s.role || !s.classType || !s.program || !s.joinedCEA
      ) {
        showError(`Please complete all data for ${label}!`);
        return false;
      }
      if (s.role === "Others" && !s.roleOther) {
        showError(`Please specify the role in church for ${label}!`);
        return false;
      }
      if (s.program === "1 Year Program" && !s.tshirtSize) {
        showError(`Please select the T-shirt size for ${label}!`);
        return false;
      }
      if (s.program === "1 Month Course" && s.classes.className.length === 0) {
        showError(`Please select at least one class for ${label}!`);
        return false;
      }
      if (s.joinedCEA === "Yes" && !s.verified) {
        showError(`Please verify the previous registration email for ${label}!`);
        return false;
      }
      if (
        s.joinedCEA === "No" &&
        s.email.trim().toLowerCase() !== (s.confirmEmail || "").trim().toLowerCase()
      ) {
        showError(`The email and confirm email do not match for ${label}!`);
        return false;
      }
    }
    const emails = students.map((s) => s.email.toLowerCase().trim());
    if (new Set(emails).size !== emails.length) {
      showError("There are duplicate student emails in this form!");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const result = await Swal.fire({
      text: `Submit registration for ${students.length} student${students.length > 1 ? "s" : ""} from ${churchName}?`,
      icon: "question",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      customClass: {
        confirmButton: "custom-btn-sweetalrt",
      },
    });
    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      const payload = {
        churchName,
        picName,
        picEmail,
        picPhone,
        churchCity,
        paymentPlan,
        students: students.map(({ collapsed, roleOther, emailVerify, verified, verifying, oldClassList, ...s }) => ({
          ...s,
          email: s.email.trim(),
          role: s.role === "Others" ? roleOther.trim() : s.role,
          tshirtSize: s.program === "1 Year Program" ? s.tshirtSize : "",
          yearJoined: s.joinedCEA === "Yes" ? s.yearJoined : "",
        })),
      };

      await axios.post("/churchregistration", payload);
      setIsLoading(false);

      const confirmBtn = await Swal.fire({
        text: "Registration Submitted.",
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
      if (confirmBtn.isConfirmed) {
        navigate("/");
      }
    } catch (error) {
      setIsLoading(false);
      showError(error.response?.data?.msg || "Something went wrong");
    }
  };

  if (isLoading) {
    return (
      <SyncLoader
        color={"#fb3104"}
        size={10}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
    );
  }

  const inputStyle = { width: "100%" };

  // Preview kelas & batch untuk 1 Year Program. Student baru: seluruh isi
  // s.classes.className berstatus "requested". Student returning (verified):
  // classList sudah di-merge dengan riwayat lama, jadi kelas yang sudah
  // "completed" ditampilkan terpisah (read-only), sisanya (masih "requested")
  // dipakai untuk preview batch — mengikuti pola yang sama dengan RegisterYearly.js.
  const renderYearlyBatchPreview = (s) => {
    const completedClasses = s.classes.className.filter((c) => c.status === "completed");
    const requestedClasses = s.classes.className.filter((c) => c.status !== "completed");
    const startIdx = classOrder.indexOf(s.startingClass);
    const currentClassIdx = classOrder.indexOf(currentClass);
    const isLateForCurrentBatch = currentClassIdx > 0 && startIdx < currentClassIdx;

    const renderList = (items) =>
      items.map((item) => {
        const detail = classDetails.find((c) => c.code === item.name);
        return <li key={item.name}>{detail ? detail.name : item.name}</li>;
      });

    const completedBlock = completedClasses.length > 0 && (
      <div className="custom-margin-form mt-4">
        <label className="form-label">Classes Already Completed</label>
        <ol>{renderList(completedClasses)}</ol>
      </div>
    );

    if (isLateForCurrentBatch) {
      const lateNextBatchClasses = requestedClasses.filter((c) => classOrder.indexOf(c.name) >= startIdx);
      const lateBatchPlusTwoClasses = requestedClasses.filter((c) => classOrder.indexOf(c.name) < startIdx);
      return (
        <>
          {completedBlock}
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum + 1}</label>
            <ol>{renderList(lateNextBatchClasses)}</ol>
          </div>
          {lateBatchPlusTwoClasses.length > 0 && (
            <div className="custom-margin-form mt-4">
              <label className="form-label">Classes to follow in Batch {batchNum + 2}</label>
              <ol>{renderList(lateBatchPlusTwoClasses)}</ol>
            </div>
          )}
        </>
      );
    }

    const currentBatchRequested = requestedClasses.filter((c) => classOrder.indexOf(c.name) >= startIdx);
    const nextBatchRequested = requestedClasses.filter((c) => classOrder.indexOf(c.name) < startIdx);

    if (nextBatchRequested.length > 0) {
      return (
        <>
          {completedBlock}
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum}</label>
            <ol>{renderList(currentBatchRequested)}</ol>
          </div>
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum + 1}</label>
            <ol>{renderList(nextBatchRequested)}</ol>
          </div>
        </>
      );
    }

    return (
      <>
        {completedBlock}
        <div className="custom-margin-form mt-4">
          <label className="form-label">Classes to follow in Batch {batchNum}</label>
          <ol>{renderList(requestedClasses)}</ol>
        </div>
      </>
    );
  };

  return (
    <main style={{ backgroundColor: "#f4f4f4", minHeight: "100vh", paddingBottom: "4rem" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "16px 5%",
          backgroundColor: "#fff",
          borderBottom: "3px solid #fb3104",
        }}
      >
        <img src={LogoCEA} alt="CEA" style={{ width: "44px", height: "44px" }} />
        <div>
          <h5 style={{ margin: 0, fontWeight: 800, color: "#1a1a1a" }}>CEA To Church Registration</h5>
          {/* <p style={{ margin: 0, fontSize: "12.5px", color: "#888" }}>
            We count your trust as a privilege that we will not take for granted! Please reach out to<a href="https://wa.me/628984001333" target="_blank" style="color:#fb3104;font-weight:600;">Whatsapp</a>. should you have any question.
          </p> */}
          <p style={{ margin: 0, fontSize: "12.5px", color: "#888" }}>
  We count your trust as a privilege that we will not take for granted! Please
  reach out to{" "}
  <a
    href="https://wa.me/628984001333"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#fb3104", fontWeight: 600 }}
  >
    Whatsapp
  </a>
  {" "}should you have any question.
</p>
        </div>
      </div>

      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "32px 16px 0" }}>
        <form>
          {/* ══════════ CHURCH / PIC SECTION ══════════ */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "24px",
              marginBottom: "24px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <h6 style={{ fontWeight: 800, color: "#fb3104", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "18px" }}>
              Church &amp; Contact Person
            </h6>
            <div className="custom-margin-form">
              <label className="form-label">Church Name</label>
              <input
                type="text"
                className="form-control custom-con-input"
                style={inputStyle}
                placeholder="Church Name"
                value={churchName}
                onChange={(e) => setChurchName(e.target.value)}
              />
            </div>
            <div className="custom-margin-form">
              <label className="form-label">Contact Person Name</label>
              <input
                type="text"
                className="form-control custom-con-input"
                style={inputStyle}
                placeholder="Full Name"
                value={picName}
                onChange={(e) => setPicName(e.target.value)}
              />
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="custom-margin-form">
                  <label className="form-label">Contact Person Email</label>
                  <input
                    type="email"
                    className="form-control custom-con-input"
                    style={inputStyle}
                    placeholder="example@gmail.com"
                    value={picEmail}
                    onChange={(e) => setPicEmail(e.target.value)}
                  />
                </div>
                <div className="custom-margin-form">
                  <label className="form-label">Confirm Contact Person Email</label>
                  <input
                    type="email"
                    className="form-control custom-con-input"
                    style={inputStyle}
                    placeholder="Re-type the email"
                    value={picConfirmEmail}
                    onChange={(e) => setPicConfirmEmail(e.target.value)}
                    onPaste={(e) => e.preventDefault()}
                  />
                  {picConfirmEmail !== "" &&
                    picEmail.trim().toLowerCase() !== picConfirmEmail.trim().toLowerCase() && (
                      <span style={{ color: "#dc2626", fontSize: "12px" }}>Email does not match</span>
                    )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="custom-margin-form">
                  <label className="form-label">Contact Person Whatsapp</label>
                  <input
                    type="text"
                    className="form-control custom-con-input"
                    style={inputStyle}
                    placeholder="6287xxxxxxxxx"
                    value={picPhone}
                    onChange={(e) => setPicPhone(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
              </div>
            </div>
            <div className="custom-margin-form">
              <label className="form-label">Church City</label>
              <input
                type="text"
                className="form-control custom-con-input"
                style={inputStyle}
                placeholder="City"
                value={churchCity}
                onChange={(e) => setChurchCity(e.target.value)}
              />
            </div>
            <div className="d-flex flex-column" style={{ marginBottom: 0 }}>
              <label className="form-label">Payment Option (applies to the whole registration)</label>
              <label className="custom-radio-space">
                <input
                  type="radio"
                  name="paymentPlan"
                  value="full"
                  checked={paymentPlan === "full"}
                  onChange={(e) => setPaymentPlan(e.target.value)}
                />
                &nbsp;Full Payment (pay the whole fee for all students at once,
                in a single payment now)
              </label>
              <label className="custom-radio-space">
                <input
                  type="radio"
                  name="paymentPlan"
                  value="installment"
                  checked={paymentPlan === "installment"}
                  onChange={(e) => setPaymentPlan(e.target.value)}
                />
                &nbsp;Installment (2-class down payment now per 1 Year Program student, remaining classes billed monthly)
              </label>
            </div>
          </div>

          {/* ══════════ STUDENT CARDS ══════════ */}
          {students.map((s, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                marginBottom: "20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              {/* Card header */}
              <div
                onClick={() => toggleCollapse(idx)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 24px",
                  backgroundColor: "#231f20",
                  cursor: "pointer",
                }}
              >
                <span style={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>
                  Student {idx + 1}
                  {s.name ? <span style={{ color: "#fb8368" }}> — {s.name}</span> : ""}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  {students.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStudent(idx);
                      }}
                      style={{
                        background: "#fb3104",
                        border: "none",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "3px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  )}
                  <span style={{ color: "#fff", fontSize: "13px" }}>
                    {s.collapsed ? "▼" : "▲"}
                  </span>
                </span>
              </div>

              {/* Card body */}
              {!s.collapsed && (
                <div style={{ padding: "24px" }}>
                  <div className="d-flex flex-column">
                    <label className="form-label">Have you ever joined Cutting Edge Academy before?</label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`joined-${idx}`}
                        value="No"
                        checked={s.joinedCEA === "No"}
                        onChange={(e) =>
                          updateStudentFields(idx, {
                            joinedCEA: e.target.value,
                            verified: false,
                            emailVerify: "",
                            oldClassList: null,
                          })
                        }
                      />
                      &nbsp;No
                    </label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`joined-${idx}`}
                        value="Yes"
                        checked={s.joinedCEA === "Yes"}
                        onChange={(e) => updateStudentFields(idx, { joinedCEA: e.target.value, verified: false })}
                      />
                      &nbsp;Yes
                    </label>
                  </div>

                  {s.joinedCEA === "Yes" && (
                    <>
                      {!s.verified && (
                        <div className="custom-margin-form mt-3">
                          <label className="form-label">Email Used in Previous Registration</label>
                          <div className="d-flex" style={{ gap: "8px" }}>
                            <input
                              type="email"
                              className="form-control custom-con-input"
                              style={inputStyle}
                              placeholder="example@gmail.com"
                              value={s.emailVerify}
                              onChange={(e) => updateStudent(idx, "emailVerify", e.target.value)}
                            />
                            <button
                              type="button"
                              style={{
                                background: "#fb3104",
                                color: "#fff",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                border: "none",
                                borderRadius: "4px",
                                padding: "0 18px",
                              }}
                              disabled={s.verifying}
                              onClick={() => handleVerify(idx)}
                            >
                              {s.verifying ? "Checking..." : "Verify"}
                            </button>
                          </div>
                        </div>
                      )}

                      {s.verified && (
                        <p className="mt-2 mb-0" style={{ color: "#16a34a", fontWeight: 600, fontSize: "14px" }}>
                          Verified — data below has been auto-filled from your previous registration.
                        </p>
                      )}
                    </>
                  )}

                  <hr className="my-4" />

                  <div className="custom-margin-form">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control custom-con-input"
                      style={inputStyle}
                      placeholder="Full Name"
                      value={s.name}
                      onChange={(e) => updateStudent(idx, "name", e.target.value)}
                    />
                  </div>
                  <div className="custom-margin-form">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control custom-con-input"
                      style={inputStyle}
                      placeholder="example@gmail.com"
                      value={s.email}
                      onChange={(e) => updateStudentFields(idx, { email: e.target.value, verified: false })}
                    />
                  </div>
                  {s.joinedCEA === "No" && (
                    <div className="custom-margin-form">
                      <label className="form-label">Confirm Email</label>
                      <input
                        type="email"
                        className="form-control custom-con-input"
                        style={inputStyle}
                        placeholder="Re-type the email"
                        value={s.confirmEmail}
                        onChange={(e) => updateStudent(idx, "confirmEmail", e.target.value)}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {s.confirmEmail !== "" &&
                        (s.email || "").trim().toLowerCase() !==
                          (s.confirmEmail || "").trim().toLowerCase() && (
                          <span style={{ color: "#dc2626", fontSize: "12px" }}>
                            Email does not match
                          </span>
                        )}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="custom-margin-form">
                        <label className="form-label">Whatsapp Number</label>
                        <input
                          type="text"
                          className="form-control custom-con-input"
                          style={inputStyle}
                          placeholder="6287xxxxxxxxx"
                          value={s.phoneNumber}
                          onChange={(e) => updateStudent(idx, "phoneNumber", e.target.value.replace(/\D/g, ""))}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="custom-margin-form">
                        <label className="form-label">Date of Birth</label>
                        <DateInput
                          className="form-control custom-con-input"
                          style={inputStyle}
                          value={s.dateOfBirth}
                          onChange={(e) => updateStudent(idx, "dateOfBirth", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="custom-margin-form">
                    <label className="form-label">Address (Indonesia Address)</label>
                    <textarea
                      rows="2"
                      className="form-control form-control-textarea"
                      placeholder="Insert address"
                      value={s.address}
                      onChange={(e) => updateStudent(idx, "address", e.target.value)}
                    ></textarea>
                  </div>
                  <div className="custom-margin-form">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control custom-con-input"
                      style={inputStyle}
                      placeholder="City"
                      value={s.city}
                      onChange={(e) => updateStudent(idx, "city", e.target.value)}
                    />
                  </div>
                  <div className="row align-items-center">
                    <div className="col-6">
                      <div className="custom-margin-form">
                        <label className="form-label">Province</label>
                        <input
                          type="text"
                          className="form-control custom-con-input"
                          style={inputStyle}
                          placeholder="Province"
                          value={s.province}
                          onChange={(e) => updateStudent(idx, "province", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="custom-margin-form">
                        <label className="form-label">Postal Code</label>
                        <input
                          type="text"
                          className="form-control custom-con-input"
                          style={inputStyle}
                          placeholder="Postal Code"
                          value={s.postalCode}
                          onChange={(e) => updateStudent(idx, "postalCode", e.target.value.replace(/\D/g, ""))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column mt-4">
                    <label className="form-label">Student Package</label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`sp-${idx}`}
                        value="The House Convention Hall"
                        checked={s.studentPackage === "The House Convention Hall"}
                        onChange={(e) => updateStudent(idx, "studentPackage", e.target.value)}
                      />
                      &nbsp;The House Convention Hall
                    </label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`sp-${idx}`}
                        value="Shipping"
                        checked={s.studentPackage === "Shipping"}
                        onChange={(e) => updateStudent(idx, "studentPackage", e.target.value)}
                      />
                      &nbsp;Shipping (only for student outside Bandung)
                    </label>
                  </div>

                  <div className="custom-margin-form mt-4">
                    <label className="form-label">Role in Church</label>
                    <select
                      className="form-select custom-con-input"
                      style={inputStyle}
                      value={s.role}
                      onChange={(e) => updateStudent(idx, "role", e.target.value)}
                    >
                      <option value="">Select Role</option>
                      <option value="HITS/Mth Student">HITS/Mth Student</option>
                      <option value="Pastoral">Pastoral</option>
                      <option value="Staff">Staff</option>
                      <option value="Coach">Coach</option>
                      <option value="Care Group Leader (CGL)">
                        Care Group Leader (CGL)
                      </option>
                      <option value="Core Team Leader (CTL)">
                        Core Team Leader (CTL)
                      </option>
                      <option value="Attendees (IFGF BDG)">
                        Attendees (IFGF BDG)
                      </option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  {s.role === "Others" && (
                    <div className="custom-margin-form mt-4">
                      <label className="form-label">Please Specify Role</label>
                      <input
                        type="text"
                        className="form-control custom-con-input"
                        style={inputStyle}
                        placeholder="Input Role in Church"
                        value={s.roleOther}
                        onChange={(e) => updateStudent(idx, "roleOther", e.target.value)}
                      />
                    </div>
                  )}

                  <div className="d-flex flex-column mt-4">
                    <label className="form-label">Online / Onsite</label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`classType-${idx}`}
                        value="Online"
                        checked={s.classType === "Online"}
                        onChange={(e) => updateStudent(idx, "classType", e.target.value)}
                      />
                      &nbsp;Online
                    </label>
                    <label className="custom-radio-space">
                      <input
                        type="radio"
                        name={`classType-${idx}`}
                        value="Onsite"
                        checked={s.classType === "Onsite"}
                        onChange={(e) => updateStudent(idx, "classType", e.target.value)}
                      />
                      &nbsp;Onsite
                    </label>
                  </div>

                  <div className="custom-margin-form mt-4">
                    <label className="form-label">Program</label>
                    <select
                      className="form-select custom-con-input"
                      style={inputStyle}
                      value={s.program}
                      onChange={(e) => handleProgramChange(idx, e.target.value)}
                    >
                      <option value="">Select Program</option>
                      <option value="1 Year Program">1 Year Program</option>
                      <option value="1 Month Course">1 Month Course</option>
                    </select>
                  </div>

                  {s.program === "1 Year Program" && (
                    <>
                      <div className="custom-margin-form mt-4">
                        <label className="form-label">Starting Class</label>
                        <select
                          className="form-select custom-con-input"
                          style={inputStyle}
                          value={s.startingClass}
                          onChange={(e) => updateStudent(idx, "startingClass", e.target.value)}
                        >
                          {(s.verified ? s.classes.className.filter((c) => c.status !== "completed") : yearlyDefaultClasses).map((c) => {
                            const detail = classDetails.find((d) => d.code === c.name);
                            return (
                              <option key={c.name} value={c.name} disabled={isStartingClassDisabled(s, c.name)}>
                                {detail ? detail.name : c.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {renderYearlyBatchPreview(s)}

                      <div className="custom-margin-form mt-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
                          <label className="form-label mb-0">Tshirt Size</label>
                          <span
                            className="size-chart-trigger"
                            onClick={() => setShowSizeChart(true)}
                          >
                            📏 Size Chart
                          </span>
                        </div>
                        <select
                          className="form-select custom-con-input"
                          style={inputStyle}
                          value={s.tshirtSize}
                          onChange={(e) => updateStudent(idx, "tshirtSize", e.target.value)}
                        >
                          <option value="">Select Size</option>
                          <option value="s">S</option>
                          <option value="m">M</option>
                          <option value="l">L</option>
                          <option value="xl">XL</option>
                          <option value="xxl">XXL</option>
                        </select>
                      </div>
                    </>
                  )}

                  {s.program === "1 Month Course" && (
                    <div className="form-check mt-4" style={{ paddingLeft: 0 }}>
                      <label className="form-label">Which class would you like to join ?</label>
                      {s.verified && s.classes.className.some((cl) => cl.status === "completed") && (
                        <p style={{ fontSize: "13px", color: "#16a34a", marginBottom: "8px" }}>
                          Already completed (no charge, not shown below):{" "}
                          {s.classes.className
                            .filter((cl) => cl.status === "completed")
                            .map((cl) => (classDetails.find((d) => d.code === cl.name) || {}).name || cl.name)
                            .join(", ")}
                        </p>
                      )}
                      {classDetails
                        .filter((c) => !["MASTERCLASS", "CONFERENCE"].includes(c.code))
                        .filter((c) => !s.classes.className.some((cl) => cl.name === c.code && cl.status === "completed"))
                        .map((c) => {
                          const currentIdx = classOrder.indexOf(currentClass);
                          const isPassed = currentIdx > 0 && classOrder.indexOf(c.code) < currentIdx;
                          return (
                            <div className="custom-text-checkbox" key={c.code} style={{ marginLeft: "1.5em" }}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                value={c.code}
                                checked={s.classes.className.some((cl) => cl.name === c.code)}
                                onChange={() => handleClassCheckbox(idx, c.code)}
                              />
                              <label className="form-check-label">&nbsp;{c.className}</label>
                              {isPassed && (
                                <span style={{ fontSize: "12px", color: "#fb3104", marginLeft: "8px" }}>
                                  Sudah lewat, akan dijadwalkan batch berikutnya
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                </div>
              )}
            </div>
          ))}

          {/* ══════════ ADD STUDENT + SUBMIT ══════════ */}
          <button
            type="button"
            onClick={addStudent}
            style={{
              width: "100%",
              backgroundColor: "#fff",
              border: "2px dashed #fb3104",
              color: "#fb3104",
              borderRadius: "10px",
              padding: "14px",
              fontWeight: 700,
              fontSize: "14px",
              cursor: "pointer",
              marginBottom: "28px",
            }}
          >
            + Add Student
          </button>

          <div style={{ textAlign: "center" }}>
            <button
              type="submit"
              className="btn custom-btn-regis"
              onClick={handleSubmit}
            >
              Submit Registration ({students.length} Student{students.length > 1 ? "s" : ""})
            </button>
          </div>
        </form>
      </div>

      <SizeChartModal show={showSizeChart} onClose={() => setShowSizeChart(false)} />
    </main>
  );
};

export default CeaToChurch;
